import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import IconLoader from '../../icons/loader';
import ButtonSecondary from '../../button/button-secondary';
import { ButtonSize } from '../../button/types';
import Textarea from '../../textarea/textarea';
import { TranscationCommentsTypes } from './types';
import ButtonPseudo from '../../button/button-pseudo';
import TransactionComment from './comment';
import IconChangeLogClock from '../../icons/change-log-clock';

import styles from './comments.module.css';
import Popup from '../../popup/popup';

export default function TransactionComments({
  comments,
  getCommentsList,
  isLoading,
  isFetching,
  pagination,
  commentsAvailable,
  createAvailable,
  editAvailable,
  deleteAvailable,
  onAddCommentToTransaction,
  addCommentInProgress,
  addCommentIsSuccess,
  onEditCommentInTransaction,
  editTransactionCommentInProgress,
  editTrancastionCommentIsSuccess,
  users,
  onDeleteCommentInTransaction,
  deleteCommentInTransactionInProgress,
  deleteCommentInTranscationIsSuccess,
  changeLogsAvailable,
}: TranscationCommentsTypes) {
  const [textareaVisible, setTextareaVisible] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [
    deleteCommentConfirmationVisible,
    setDeleteCommentConfirmationVisible,
  ] = useState(false);
  const [selectedComment, setSelectedComment] = useState('');

  useEffect(() => {
    if (addCommentIsSuccess) {
      setCommentText('');
      setTextareaVisible(false);
    }
  }, [addCommentIsSuccess]);

  useEffect(() => {
    if (deleteCommentInTranscationIsSuccess) {
      setDeleteCommentConfirmationVisible(false);
      setSelectedComment('');
    }
  }, [deleteCommentInTranscationIsSuccess]);

  let content;

  if (!commentsAvailable) {
    content = (
      <div className={styles.emptyComments}>
        You don&apos;t have access to this section
      </div>
    );
  } else if (commentsAvailable) {
    if (isLoading || (isFetching && !comments)) {
      content = (
        <div className={styles.contentLoader}>
          <IconLoader />
        </div>
      );
    } else if (!isLoading && !comments?.length) {
      content = (
        <div className={styles.emptyComments}>There are no comments</div>
      );
    } else if (!isLoading && comments) {
      content = (
        <>
          {comments?.map((item) => (
            <TransactionComment
              key={item.id}
              comment={item}
              onEditCommentInTransaction={onEditCommentInTransaction}
              editTrancastionCommentIsSuccess={editTrancastionCommentIsSuccess}
              editTransactionCommentInProgress={
                editTransactionCommentInProgress
              }
              editAvailable={editAvailable}
              deleteAvailable={deleteAvailable}
              user={users?.find((user) => user.value === item?.userId)}
              setDeleteCommentConfirmationVisible={
                setDeleteCommentConfirmationVisible
              }
              setSelectedComment={setSelectedComment}
            />
          ))}
          {pagination?.hasNextPage && getCommentsList && (
            <div className={styles.paginationWrapper}>
              <ButtonSecondary
                text={isFetching ? 'Loading...' : 'Show more'}
                size={ButtonSize.SMALL}
                className="button-white"
                onClick={() => getCommentsList(pagination?.endCursor)}
              />
            </div>
          )}
        </>
      );
    }
  }

  return (
    <div className={styles.commentsWrapper}>
      <div className={styles.commentsTop}>
        <div className={styles.commentsTitle}>
          Comments
          {changeLogsAvailable && (
            <NavLink
              to="/administration/change-logs?entityType=TRANSACTION_COMMENT"
              target="_blank"
              className={styles.changeLogLink}
            >
              <IconChangeLogClock />
              Change Log
            </NavLink>
          )}
        </div>
        {createAvailable && !textareaVisible && (
          <ButtonSecondary
            size={ButtonSize.SMALL}
            text="Add comment"
            onClick={() => setTextareaVisible(true)}
            className={styles.createCommentButton}
          />
        )}
        {textareaVisible && (
          <div className={styles.textareaWrapper}>
            <Textarea
              name="commentText"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Enter comment..."
              className={styles.commentTextarea}
            />
            <div className={styles.createCommentControls}>
              <ButtonSecondary
                size={ButtonSize.SMALL}
                className={`button-white ${styles.sendCommentButton}`}
                text={addCommentInProgress ? 'Loading...' : 'Save comment'}
                disabled={addCommentInProgress}
                onClick={() =>
                  onAddCommentToTransaction &&
                  onAddCommentToTransaction(commentText)
                }
              />
              <ButtonPseudo
                size={ButtonSize.SMALL}
                text="Cancel"
                onClick={() => {
                  setCommentText('');
                  setTextareaVisible(false);
                }}
                disabled={addCommentInProgress}
              />
            </div>
          </div>
        )}
      </div>
      <div className={styles.commentsItemsList}>{content}</div>
      <Popup
        title="Delete comment?"
        onCancelClick={() => setDeleteCommentConfirmationVisible(false)}
        onOkClick={() =>
          onDeleteCommentInTransaction &&
          onDeleteCommentInTransaction(selectedComment)
        }
        cancelButtonText="Cancel"
        okButtonText={
          deleteCommentInTransactionInProgress ? 'Loading...' : 'Delete'
        }
        isVisible={deleteCommentConfirmationVisible}
        isLoading={deleteCommentInTransactionInProgress}
        parent={document.getElementById('delete-popup-wrapper') as HTMLElement}
      />
    </div>
  );
}
